import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Tag, FileText, CreditCard, Settings,
  LogOut, ChevronRight, TrendingUp, TrendingDown, AlertCircle,
  CheckCircle, XCircle, Plus, Search, Download, Edit2, Trash2,
  DollarSign, BarChart3, Calendar, Eye, ArrowLeft, Crown, ToggleLeft, ToggleRight,
  Globe, Upload, Images, X as XIcon, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import DeliveryPanel from './DeliveryPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ ADMIN LAYOUT ============

const AdminLayout = ({ children, activeTab }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { id: 'users', label: 'Users', icon: Users, path: '/admin/users' },
    { id: 'promo-codes', label: 'Promo Codes', icon: Tag, path: '/admin/promo-codes' },
    { id: 'audits', label: 'All Audits', icon: FileText, path: '/admin/audits' },
    { id: 'billing', label: 'Billing', icon: CreditCard, path: '/admin/billing' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[var(--ink-1)] text-white flex flex-col fixed h-full">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#E94560] to-[#0F3460] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">UX</span>
            </div>
            <span className="font-bold text-lg">Admin</span>
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === item.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:bg-white/5 hover:text-white/80'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <Users className="w-4 h-4 text-white/50" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-white/50 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
          <Link
            to="/dashboard"
            className="w-full flex items-center gap-3 px-4 py-2 mt-2 rounded-lg text-sm text-white/50 hover:bg-white/5 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Link>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
};

// ============ DASHBOARD PAGE ============

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const resp = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(resp.data);
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <AdminLayout activeTab="dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#E94560] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[var(--ink-1)]">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">Overview of your UXAuditPro platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          icon={Users}
          color="bg-blue-500"
          subtitle={`${stats?.users?.by_plan?.PRO || 0} Pro, ${stats?.users?.by_plan?.STARTER || 0} Starter`}
        />
        <StatCard
          title="Total Audits"
          value={stats?.audits?.total || 0}
          icon={FileText}
          color="bg-emerald-500"
          subtitle={`${stats?.audits?.success || 0} completed, ${stats?.audits?.failed || 0} failed`}
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${(stats?.revenue?.monthly || 0).toFixed(2)}`}
          icon={DollarSign}
          color="bg-purple-500"
          subtitle={`${stats?.revenue?.transactions || 0} transactions total`}
        />
        <StatCard
          title="Active Promos"
          value={stats?.promo_codes?.active || 0}
          icon={Tag}
          color="bg-amber-500"
          subtitle="Active promo codes"
        />
      </div>

      {/* Audit Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="font-bold text-[var(--ink-1)] mb-4">Audit Status Breakdown</h3>
          <div className="space-y-4">
            <StatusBar label="Completed" value={stats?.audits?.success || 0} total={stats?.audits?.total || 1} color="bg-emerald-500" />
            <StatusBar label="Processing" value={stats?.audits?.pending || 0} total={stats?.audits?.total || 1} color="bg-blue-500" />
            <StatusBar label="Failed" value={stats?.audits?.failed || 0} total={stats?.audits?.total || 1} color="bg-red-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="font-bold text-[var(--ink-1)] mb-4">Users by Plan</h3>
          <div className="space-y-4">
            <StatusBar label="Free" value={stats?.users?.by_plan?.FREE || 0} total={stats?.users?.total || 1} color="bg-slate-400" />
            <StatusBar label="Starter" value={stats?.users?.by_plan?.STARTER || 0} total={stats?.users?.total || 1} color="bg-blue-500" />
            {/* Not "Pro" and "Enterprise", which were two rows guaranteed to
                read 0 once those tiers were deleted. This row is the one worth
                having: any account on a plan the product does not define. It
                should always be zero, and if it is not, server.py has already
                logged the plan string it saw. */}
            <StatusBar
              label="Unrecognised plan"
              value={Math.max(0, (stats?.users?.total || 0)
                - (stats?.users?.by_plan?.FREE || 0)
                - (stats?.users?.by_plan?.STARTER || 0))}
              total={stats?.users?.total || 1}
              color="bg-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h3 className="font-bold text-[var(--ink-1)] mb-4">Revenue Summary</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[var(--ink-1)]">${(stats?.revenue?.total || 0).toFixed(2)}</p>
            <p className="text-sm text-slate-400 mt-1">Total Revenue</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[var(--ink-1)]">${(stats?.revenue?.monthly || 0).toFixed(2)}</p>
            <p className="text-sm text-slate-400 mt-1">This Month</p>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-2xl font-bold text-[var(--ink-1)]">{stats?.revenue?.transactions || 0}</p>
            <p className="text-sm text-slate-400 mt-1">Total Transactions</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className="text-3xl font-bold text-[var(--ink-1)] mt-1">{value}</p>
        {subtitle && <p className="text-sm text-slate-400 mt-2">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} bg-opacity-10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
      </div>
    </div>
  </div>
);

const StatusBar = ({ label, value, total, color }) => {
  const percent = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-[var(--ink-1)]">{value}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

// ============ USERS PAGE ============

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();

  const fetchUsers = async (searchTerm = '') => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/users?search=${searchTerm}`, { withCredentials: true });
      setUsers(resp.data.users);
      setTotal(resp.data.total);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = () => {
    fetchUsers(search);
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan: newPlan }, { withCredentials: true });
      fetchUsers(search);
      setSelectedUser(null);
    } catch (e) {
      alert('Failed to update plan');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This will deactivate their account and invalidate all sessions.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      fetchUsers(search);
      setSelectedUser(null);
    } catch (e) {
      alert('Failed to delete user');
    }
  };

  return (
    <AdminLayout activeTab="users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ink-1)]">User Management</h1>
          <p className="text-slate-400 text-sm mt-1">{total} total users</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E94560]/20"
              data-testid="admin-user-search"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-[var(--ink-1)] text-white rounded-lg text-sm font-medium hover:bg-[#2a2a4e] transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">User</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Plan</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Audits</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Spent</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Joined</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-400">Loading...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-400">No users found</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.user_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-[var(--ink-1)]">{user.name || 'No name'}</p>
                        <p className="text-sm text-slate-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${
                      user.plan === 'STARTER' ? 'bg-blue-100 text-blue-700' :
                      user.plan === 'FREE' ? 'bg-slate-100 text-slate-600' :
                      // Anything else is a plan that should not exist. Amber so
                      // it reads as "look at this", not as a tier.
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {user.plan || 'FREE'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{user.audit_count || 0}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">${(user.total_spent || 0).toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm text-slate-400">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-slate-400 hover:text-[var(--ink-1)] p-1"
                        data-testid={`user-edit-${user.user_id}`}
                        title="Edit user plan"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.user_id, user.name || user.email)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        data-testid={`user-delete-${user.user_id}`}
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-[var(--ink-1)] mb-4">Edit User Plan</h3>
            <div className="mb-4">
              <p className="text-sm text-slate-600">{selectedUser.name}</p>
              <p className="text-sm text-slate-400">{selectedUser.email}</p>
            </div>
            <div className="space-y-2 mb-6">
              {/* Only the plans that exist. PRO and ENTERPRISE were grantable
                  here until 2026-08-31 — an admin click was the one way to
                  create an account the enforcement path had no limits for. */}
              {['FREE', 'STARTER'].map((plan) => (
                <button
                  key={plan}
                  onClick={() => handleUpdatePlan(selectedUser.user_id, plan)}
                  className={`w-full py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${
                    selectedUser.plan === plan
                      ? 'border-[#E94560] bg-[#E94560]/5 text-[#E94560]'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {plan}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setSelectedUser(null)}
                className="flex-1 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(selectedUser.user_id, selectedUser.name || selectedUser.email)}
                className="flex-1 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg font-medium transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// ============ PROMO CODES PAGE ============

const PromoCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // applies_to defaults to 'audit' (owner, 2026-10-09). A code saved before
  // the field existed has none and is whole order (backend promo.applies_to).
  const blankCode = { code: '', discount_percent: 10, max_uses: '', valid_until: '', applies_to: 'audit', restrict_email: '' };
  const [newCode, setNewCode] = useState(blankCode);

  const fetchCodes = async () => {
    try {
      const resp = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setCodes(resp.data.promo_codes);
    } catch (e) {
      console.error('Failed to fetch promo codes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: newCode.code,
        discount_percent: parseInt(newCode.discount_percent),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        valid_until: newCode.valid_until || null,
        applies_to: newCode.applies_to,
        restrict_email: newCode.restrict_email.trim() || null
      }, { withCredentials: true });
      setShowCreate(false);
      setNewCode(blankCode);
      fetchCodes();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleToggle = async (codeId, isActive) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${codeId}`, { is_active: !isActive }, { withCredentials: true });
      fetchCodes();
    } catch (e) {
      alert('Failed to update promo code');
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${codeId}`, { withCredentials: true });
      fetchCodes();
    } catch (e) {
      alert('Failed to delete promo code');
    }
  };

  return (
    <AdminLayout activeTab="promo-codes">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ink-1)]">Promo Codes</h1>
          <p className="text-slate-400 text-sm mt-1">Manage discount codes</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#E94560] text-white rounded-lg text-sm font-medium hover:bg-[#E94560]/90 transition-colors"
          data-testid="create-promo-btn"
        >
          <Plus className="w-4 h-4" /> Create Code
        </button>
      </div>

      {/* Codes Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Code</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Discount</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Applies to</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Restricted to</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Uses</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Valid Until</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-slate-400">Loading...</td>
              </tr>
            ) : codes.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-slate-400">No promo codes yet</td>
              </tr>
            ) : (
              codes.map((code) => (
                <tr key={code.code_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="font-mono font-bold text-[var(--ink-1)]">{code.code}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-emerald-600 font-medium">{code.discount_percent}% off</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {code.applies_to === 'audit' ? 'Audit only' : 'Whole order'}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {code.restrict_email || (code.user_id ? `Account ${code.user_id}` : 'Anyone')}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {code.times_used || 0} / {code.max_uses || '∞'}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-400">
                    {code.valid_until ? new Date(code.valid_until).toLocaleDateString() : 'No expiry'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${
                      code.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => handleToggle(code.code_id, code.is_active)}
                      className="text-slate-400 hover:text-[var(--ink-1)] p-1 mr-2"
                    >
                      {code.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(code.code_id)}
                      className="text-slate-400 hover:text-red-500 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-[var(--ink-1)] mb-4">Create Promo Code</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm text-slate-500 font-medium mb-1 block">Code</label>
                <input
                  type="text"
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER20"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="promo-code-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium mb-1 block">Discount %</label>
                <input
                  type="number"
                  value={newCode.discount_percent}
                  onChange={(e) => setNewCode({ ...newCode, discount_percent: e.target.value })}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium mb-1 block">Max Uses (optional)</label>
                <input
                  type="number"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-slate-500 font-medium mb-1 block">Valid Until (optional)</label>
                <input
                  type="date"
                  value={newCode.valid_until}
                  onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <fieldset>
                <legend className="text-sm text-slate-500 font-medium mb-1">Applies to</legend>
                {[
                  ['audit', 'Audit only', 'Discounts the audit price. Add-ons are charged in full.'],
                  ['order', 'Whole order', 'Discounts the audit and every add-on.'],
                ].map(([value, label, hint]) => (
                  <label key={value} className="flex items-start gap-2 py-1 text-sm text-[var(--ink-1)] cursor-pointer">
                    <input
                      type="radio"
                      name="applies_to"
                      value={value}
                      checked={newCode.applies_to === value}
                      onChange={() => setNewCode({ ...newCode, applies_to: value })}
                      className="mt-1"
                      data-testid={`promo-applies-${value}`}
                    />
                    <span><span className="font-medium">{label}</span><span className="block text-slate-500">{hint}</span></span>
                  </label>
                ))}
              </fieldset>
              <div>
                <label htmlFor="promo-restrict-email" className="text-sm text-slate-500 font-medium mb-1 block">Restrict to email (optional)</label>
                <input
                  id="promo-restrict-email"
                  type="email"
                  value={newCode.restrict_email}
                  onChange={(e) => setNewCode({ ...newCode, restrict_email: e.target.value })}
                  placeholder="Anyone can use it"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="promo-restrict-email"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 py-2 bg-[#E94560] text-white rounded-lg text-sm font-medium hover:bg-[#E94560]/90"
                data-testid="create-promo-submit"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// ============ SCREENSHOTS MODAL ============

const ScreenshotsModal = ({ auditId, appName, onClose }) => {
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchScreenshots = async () => {
      try {
        const resp = await axios.get(`${API}/audits/${auditId}/screenshots`, { withCredentials: true });
        setScreenshots(resp.data.screenshots || []);
      } catch (e) {
        setError('Could not load screenshots. They may have expired or the audit had no files.');
      } finally {
        setLoading(false);
      }
    };
    fetchScreenshots();
  }, [auditId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-bold text-[var(--ink-1)] text-base">Uploaded Files — {appName || 'Audit'}</h2>
            <p className="text-sm text-slate-400 mt-0.5">Audit ID: {auditId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <XIcon className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Loading screenshots…</div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-500 text-sm">{error}</div>
          ) : screenshots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 text-sm gap-2">
              <Images className="w-8 h-8 text-slate-300" />
              No screenshots stored for this audit.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {screenshots.map((b64, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img
                    src={`data:image/jpeg;base64,${b64}`}
                    alt={`Screenshot ${idx + 1}`}
                    className="w-full object-contain max-h-64"
                  />
                  <p className="text-center text-[10px] text-slate-400 py-1.5">Image {idx + 1} of {screenshots.length}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============ AUDITS PAGE ============

//: Ten rows a page. The endpoint's own default is 50.
const PAGE_SIZE = 10;

const AuditsPage = () => {
  const [audits, setAudits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The endpoint has always accepted skip/limit and defaulted to 50; this page
  // simply never sent them, so every visit pulled fifty rows to show a
  // seven-column table.
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [retrying, setRetrying] = useState({}); // { [audit_id]: true }
  const [retryMsg, setRetryMsg] = useState({}); // { [audit_id]: 'message' }
  const [screenshotModal, setScreenshotModal] = useState(null); // { auditId, appName }
  const [deliveryModal, setDeliveryModal] = useState(null); // { auditId, appName }

  const fetchAudits = async (status = '', search = '', pageIndex = 0) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('skip', String(pageIndex * PAGE_SIZE));
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      const resp = await axios.get(`${API}/admin/audits?${params.toString()}`, { withCredentials: true });
      setAudits(resp.data.audits || []);
      setTotal(resp.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch audits:', e);
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load audits';
      setError(msg);
      setAudits([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchAudits is intentionally not a dependency: it is redefined every
    // render and listing it would re-fetch in a loop. The lint directive that
    // would normally sit here names react-hooks/exhaustive-deps, which this
    // project cannot resolve - a bare `npm run build` fails on the one that
    // already exists in CompareAuditsPage.js:223. The deploy gate passes
    // DISABLE_ESLINT_PLUGIN=true, so it never sees either.
    fetchAudits(statusFilter, searchTerm, page);
  }, [statusFilter, searchTerm, page]);

  // Filtering from page 4 would otherwise ask for rows 30-39 of a result set
  // that might have three, and the table would come back empty with no
  // visible reason why.
  const goToFirstPage = () => setPage(0);
  const handleSearch = () => { goToFirstPage(); setSearchTerm(searchInput.trim()); };

  const handleRetry = async (auditId, appName) => {
    if (!window.confirm(`Retry audit for "${appName || auditId}"?\nThe user will be emailed when it completes.`)) return;
    setRetrying(r => ({ ...r, [auditId]: true }));
    setRetryMsg(m => ({ ...m, [auditId]: '' }));
    try {
      await axios.post(`${API}/admin/audits/${auditId}/force-retry`, {}, { withCredentials: true });
      setRetryMsg(m => ({ ...m, [auditId]: '↺ Retry started' }));
      // Refresh list after short delay so status shows PROCESSING
      setTimeout(() => fetchAudits(statusFilter, searchTerm, page), 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Retry failed';
      setRetryMsg(m => ({ ...m, [auditId]: `✗ ${msg}` }));
    } finally {
      setRetrying(r => ({ ...r, [auditId]: false }));
    }
  };

  return (
    <AdminLayout activeTab="audits">
      {screenshotModal && (
        <ScreenshotsModal
          auditId={screenshotModal.auditId}
          appName={screenshotModal.appName}
          onClose={() => setScreenshotModal(null)}
        />
      )}

      {deliveryModal && (
        <DeliveryPanel
          auditId={deliveryModal.auditId}
          appName={deliveryModal.appName}
          onClose={() => setDeliveryModal(null)}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ink-1)]">All Audits</h1>
          <p className="text-slate-400 text-sm mt-1">
            {total === 0
              ? 'No audits'
              : `Showing ${page * PAGE_SIZE + 1}\u2013${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name…"
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none w-48"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-3 py-2 bg-[var(--ink-1)] text-white text-sm rounded-lg hover:bg-[#2a2a4e]"
          >
            Search
          </button>
          <select
            value={statusFilter}
            onChange={(e) => { goToFirstPage(); setStatusFilter(e.target.value); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
            data-testid="audit-status-filter"
          >
            <option value="">All Status</option>
            <option value="DELIVERED">Completed</option>
            <option value="PROCESSING">Processing</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Audits Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Audit</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">User</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Score</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Email</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Date</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-slate-400">Loading...</td>
              </tr>
            ) : audits.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-slate-400">No audits found</td>
              </tr>
            ) : (
              audits.map((audit) => {
                const isScreenshot = audit.input_type === 'SCREENSHOT';
                return (
                  <tr key={audit.audit_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-sm text-[var(--ink-1)]">{audit.app_name || 'Unnamed'}</p>
                      {/* Input type badge + link/label */}
                      <div className="flex items-center gap-1 mt-0.5">
                        {isScreenshot ? (
                          <>
                            <Upload className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span className="text-sm text-slate-400">File upload</span>
                          </>
                        ) : audit.input_data ? (
                          <>
                            <Globe className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <a
                              href={audit.input_data}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-500 hover:underline truncate max-w-[170px]"
                              title={audit.input_data}
                            >
                              {audit.input_data}
                            </a>
                          </>
                        ) : (
                          <span className="text-sm text-slate-300">—</span>
                        )}
                      </div>
                      {/* Error message for FAILED audits */}
                      {audit.status === 'FAILED' && audit.error_message && (
                        <p
                          className="text-[10px] text-red-500 mt-1 truncate max-w-[200px]"
                          title={audit.error_message}
                        >
                          {audit.error_message}
                        </p>
                      )}
                      {/* Admin force-retry failure reason (distinct from original error) */}
                      {audit.admin_retry_outcome === 'FAILED' && audit.admin_retry_error && (
                        <p
                          className="text-[10px] text-orange-500 mt-0.5 truncate max-w-[200px]"
                          title={`Admin retry failed: ${audit.admin_retry_error}`}
                        >
                          Retry: {audit.admin_retry_error}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-slate-600">{audit.user?.name || 'Unknown'}</p>
                      <p className="text-sm text-slate-400">{audit.user?.email}</p>
                    </td>
                    <td className="py-3 px-4">
                      {audit.overall_score ? (
                        <span className={`font-bold text-lg ${
                          audit.overall_score >= 70 ? 'text-emerald-600' :
                          audit.overall_score >= 50 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {audit.overall_score}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${
                        audit.status === 'DELIVERED' ? 'bg-emerald-100 text-emerald-700' :
                        audit.status === 'PROCESSING' ? 'bg-blue-100 text-blue-700' :
                        audit.status === 'PENDING' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {audit.status}
                      </span>
                      {/* Auto-retry badge */}
                      {audit.retry_count > 0 && (
                        <span className={`ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded-full ${
                          audit.retry_outcome === 'DELIVERED' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' :
                          audit.retry_outcome === 'FAILED' ? 'bg-red-50 text-red-500 border border-red-200' :
                          'bg-amber-50 text-amber-600 border border-amber-200'
                        }`}
                          title={audit.retry_outcome ? `Auto-retry: ${audit.retry_outcome}` : 'Auto-retry scheduled'}
                        >
                          {audit.retry_outcome === 'DELIVERED' ? '↺ fixed' :
                           audit.retry_outcome === 'FAILED' ? '↺ failed' : '↺ queued'}
                        </span>
                      )}
                      {/* Admin force-retry outcome badge */}
                      {audit.admin_retry_outcome && (
                        <span className={`ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded-full ${
                          audit.admin_retry_outcome === 'DELIVERED'
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-orange-50 text-orange-600 border border-orange-200'
                        }`}
                          title={
                            audit.admin_retry_outcome === 'FAILED'
                              ? `Admin retry failed: ${audit.admin_retry_error || 'unknown error'}`
                              : 'Admin retry succeeded'
                          }
                        >
                          {audit.admin_retry_outcome === 'DELIVERED' ? 'ok' : 'failed'}
                        </span>
                      )}
                    </td>
                    {/* Email column */}
                    <td className="py-3 px-4">
                      {(() => {
                        const s = audit.email_summary;
                        if (!s || s === 'not_sent') return <span className="text-slate-300 text-sm">—</span>;
                        const cfg = {
                          delivered:            { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '✓ Sent' },
                          retry_delivered:      { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '✓ Retry sent' },
                          apology_sent:         { bg: 'bg-blue-100',    text: 'text-blue-700',    label: '✓ Apology sent' },
                          apology_failed:       { bg: 'bg-red-100',     text: 'text-red-600',     label: '✗ Apology failed' },
                          failure_notified:     { bg: 'bg-amber-100',   text: 'text-amber-700',   label: '⚠ Retry notified' },
                          retry_audit_failed:   { bg: 'bg-slate-100',   text: 'text-slate-500',   label: '— Audit failed' },
                          retry_email_failed:   { bg: 'bg-red-100',     text: 'text-red-600',     label: '✗ Send failed' },
                          failure_no_email:     { bg: 'bg-slate-100',   text: 'text-slate-400',   label: '— No email' },
                          retry_skipped_ses:    { bg: 'bg-slate-100',   text: 'text-slate-500',   label: '— SES off' },
                          skipped:              { bg: 'bg-slate-100',   text: 'text-slate-500',   label: '— SES off' },
                          error:                { bg: 'bg-red-100',     text: 'text-red-600',     label: '✗ Error' },
                        }[s] || { bg: 'bg-slate-100', text: 'text-slate-500', label: s };
                        return (
                          <span className={`px-2 py-1 text-[10px] font-medium rounded-full ${cfg.bg} ${cfg.text}`}
                            title={audit.retry_email_error || audit.email_error || ''}>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-400">
                      {audit.created_at ? new Date(audit.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {audit.status === 'DELIVERED' && (
                          <a
                            href={`/audit/${audit.audit_id}/report`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ink-1)] text-white text-sm font-medium rounded-lg hover:bg-[#2a2a4e] transition-colors"
                            data-testid={`view-report-${audit.audit_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View Report
                          </a>
                        )}
                        {/* SECOND link rather than a replacement: the admin
                            view and the customer view answer different
                            questions, and a support ticket usually needs both.
                            The server decides what `as=customer` means; this
                            only asks for it. */}
                        {audit.status === 'DELIVERED' && (
                          <a
                            href={`/audit/${audit.audit_id}/report?as=customer`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
                            data-testid={`view-as-customer-${audit.audit_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View as customer
                          </a>
                        )}
                        {isScreenshot && (
                          <button
                            onClick={() => setScreenshotModal({ auditId: audit.audit_id, appName: audit.app_name })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
                            data-testid={`view-files-${audit.audit_id}`}
                          >
                            <Images className="w-3.5 h-3.5" />
                            View Files
                          </button>
                        )}
                        {/* DELIVERY. On every row, not just DELIVERED ones:
                            "paid and the report failed" is a mismatch that only
                            exists on a FAILED audit, so hiding the panel there
                            would hide the worst case it reports. */}
                        <button
                          onClick={() => setDeliveryModal({ auditId: audit.audit_id, appName: audit.app_name })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
                          data-testid={`delivery-${audit.audit_id}`}
                          title="What this customer paid and what they received"
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                          Delivery
                        </button>
                        <button
                          onClick={() => handleRetry(audit.audit_id, audit.app_name)}
                          disabled={retrying[audit.audit_id] || audit.status === 'PROCESSING'}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`retry-${audit.audit_id}`}
                          title="Re-run audit and email user on completion"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          {retrying[audit.audit_id] ? 'Retrying…' : 'Retry Audit'}
                        </button>
                        {retryMsg[audit.audit_id] && (
                          <span className={`text-[10px] font-medium ${retryMsg[audit.audit_id].startsWith('✗') ? 'text-red-500' : 'text-emerald-600'}`}>
                            {retryMsg[audit.audit_id]}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paging. Rendered only when there is more than one page, so a small
          install never sees a control that does nothing. Disabled rather than
          hidden at the ends, so the row does not reflow as you page. */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4" data-testid="audits-pagination">
          <button
            type="button"
            onClick={() => setPage((n) => Math.max(0, n - 1))}
            disabled={page === 0 || loading}
            className="uxa-submit uxa-submit--auto uxa-submit--tertiary disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-slate-400">
            Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <button
            type="button"
            onClick={() => setPage((n) => n + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total || loading}
            className="uxa-submit uxa-submit--auto uxa-submit--tertiary disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
};

// ============ BILLING PAGE ============

const BillingPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const resp = await axios.get(`${API}/admin/billing`, { withCredentials: true });
        setTransactions(resp.data.transactions);
        setTotal(resp.data.total);
      } catch (e) {
        console.error('Failed to fetch transactions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  return (
    <AdminLayout activeTab="billing">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ink-1)]">Billing History</h1>
          <p className="text-slate-400 text-sm mt-1">{total} total transactions</p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Invoice #</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">PayPal Transaction ID</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">User</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Plan</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Amount</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400 uppercase">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-slate-400">Loading...</td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-slate-400">No transactions yet</td>
              </tr>
            ) : (
              transactions.map((tx) => {
                const rawId = tx.payment_id || '';
                const invoiceNum = rawId ? `INV-${rawId.replace(/-/g, '').toUpperCase().slice(0, 8)}` : 'N/A';
                const statusLabel = tx.status === 'completed' ? 'paid' : (tx.status || 'pending');
                const statusClass =
                  statusLabel === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                  statusLabel === 'refunded' ? 'bg-slate-100 text-slate-600' :
                  statusLabel === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700';
                return (
                <tr key={rawId || tx.created_at} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <p className="font-mono text-sm font-medium text-[var(--ink-1)]">{invoiceNum}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-mono text-sm text-slate-500 break-all">{rawId || '—'}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-slate-600">{tx.user?.name || 'Unknown'}</p>
                    <p className="text-sm text-slate-400">{tx.user?.email}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 text-[10px] font-bold rounded-full bg-purple-100 text-purple-700">
                      {tx.plan}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-medium text-[var(--ink-1)]">${(tx.amount || 0).toFixed(2)}</span>
                    {tx.discount_percent > 0 && (
                      <span className="text-sm text-emerald-600 ml-1">(-{tx.discount_percent}%)</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-400">
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

// ============ SETTINGS PAGE ============

const SettingsPage = () => {
  const [paypalMode, setPaypalMode] = useState('sandbox');
  const [sandboxConfigured, setSandboxConfigured] = useState(false);
  const [liveConfigured, setLiveConfigured] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [sandboxConfig, setSandboxConfig] = useState(null);
  const [liveConfig, setLiveConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPaymentMode();
  }, []);

  const fetchPaymentMode = async () => {
    try {
      const resp = await axios.get(`${API}/payments/admin/mode`, { withCredentials: true });
      setPaypalMode(resp.data.mode);
      setSandboxConfigured(resp.data.sandbox_configured);
      setLiveConfigured(resp.data.live_configured);
      setCurrentConfig(resp.data.current_config);
      setSandboxConfig(resp.data.sandbox_config);
      setLiveConfig(resp.data.live_config);
    } catch (e) {
      console.error('Failed to fetch payment mode:', e);
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentMode = async () => {
    const newMode = paypalMode === 'sandbox' ? 'live' : 'sandbox';
    
    if (newMode === 'live' && !liveConfigured) {
      alert('Live PayPal credentials are not configured. Please add them to the backend environment.');
      return;
    }
    
    const confirmed = window.confirm(
      newMode === 'live' 
        ? 'WARNING: You are about to enable LIVE payments. Real money will be charged. Continue?'
        : 'Switch to Sandbox mode? Test payments only.'
    );
    
    if (!confirmed) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/payments/admin/mode`, { mode: newMode }, { withCredentials: true });
      setPaypalMode(newMode);
      // Refresh to get new URLs
      await fetchPaymentMode();
      alert(`PayPal mode changed to ${newMode.toUpperCase()}`);
    } catch (e) {
      console.error('Failed to update payment mode:', e);
      alert(e.response?.data?.detail || 'Failed to update payment mode');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout activeTab="settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[var(--ink-1)]">
          Settings
        </h1>
        <p className="text-slate-400 mt-1">Configure platform settings</p>
      </div>

      {/* PayPal Mode Toggle */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6" data-testid="paypal-mode-section">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[var(--ink-1)]">
              PayPal Payment Mode
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Toggle between Sandbox (testing) and Live (production) payment processing
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Mode Display */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${paypalMode === 'live' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                <div>
                  <p className="font-medium text-[var(--ink-1)]">
                    Current Mode: <span className={paypalMode === 'live' ? 'text-emerald-600' : 'text-amber-600'}>{paypalMode.toUpperCase()}</span>
                  </p>
                  <p className="text-sm text-slate-400">
                    {paypalMode === 'live' 
                      ? 'Processing real payments with live PayPal credentials' 
                      : 'Using PayPal Sandbox for test transactions'}
                  </p>
                </div>
              </div>
              <button
                onClick={togglePaymentMode}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  paypalMode === 'live'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                } disabled:opacity-50`}
                data-testid="toggle-paypal-mode-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : paypalMode === 'live' ? (
                  <ToggleRight className="w-4 h-4" />
                ) : (
                  <ToggleLeft className="w-4 h-4" />
                )}
                Switch to {paypalMode === 'live' ? 'Sandbox' : 'Live'}
              </button>
            </div>

            {/* Current Configuration Details */}
            {currentConfig && (
              <div className="p-4 rounded-xl border border-blue-200 bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Active Configuration</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 mb-1">API Base URL</p>
                    <p className="font-mono text-blue-800 bg-white px-2 py-1 rounded">{currentConfig.api_base_url}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Checkout URL</p>
                    <p className="font-mono text-blue-800 bg-white px-2 py-1 rounded">{currentConfig.checkout_url}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Client ID</p>
                    <p className="font-mono text-blue-800 bg-white px-2 py-1 rounded">{currentConfig.client_id}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Plan IDs</p>
                    <p className="font-mono text-blue-800 bg-white px-2 py-1 rounded text-[10px]">
                      Pro: {currentConfig.plan_ids?.pro?.substring(0, 15)}...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Credentials Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl border ${sandboxConfigured ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'} ${paypalMode === 'sandbox' ? 'ring-2 ring-amber-400' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  {sandboxConfigured ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${sandboxConfigured ? 'text-emerald-700' : 'text-red-700'}`}>
                    Sandbox {paypalMode === 'sandbox' && '(Active)'}
                  </span>
                </div>
                {sandboxConfig && sandboxConfigured && (
                  <div className="text-[10px] space-y-1 text-slate-600">
                    <p><span className="text-slate-400">API:</span> {sandboxConfig.api_base_url}</p>
                    <p><span className="text-slate-400">Client:</span> {sandboxConfig.client_id}</p>
                    <p><span className="text-slate-400">Pro Plan:</span> {sandboxConfig.plan_ids?.PRO?.substring(0, 12)}...</p>
                    <p><span className="text-slate-400">Max Plan:</span> {sandboxConfig.plan_ids?.MAX?.substring(0, 12)}...</p>
                  </div>
                )}
              </div>
              <div className={`p-4 rounded-xl border ${liveConfigured ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'} ${paypalMode === 'live' ? 'ring-2 ring-emerald-400' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  {liveConfigured ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${liveConfigured ? 'text-emerald-700' : 'text-red-700'}`}>
                    Live {paypalMode === 'live' && '(Active)'}
                  </span>
                </div>
                {liveConfig && liveConfigured && (
                  <div className="text-[10px] space-y-1 text-slate-600">
                    <p><span className="text-slate-400">API:</span> {liveConfig.api_base_url}</p>
                    <p><span className="text-slate-400">Client:</span> {liveConfig.client_id}</p>
                    <p><span className="text-slate-400">Pro Plan:</span> {liveConfig.plan_ids?.PRO?.substring(0, 12) || 'Not set'}...</p>
                    <p><span className="text-slate-400">Max Plan:</span> {liveConfig.plan_ids?.MAX?.substring(0, 12) || 'Not set'}...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Warning for Live Mode */}
            {paypalMode === 'live' && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Live Mode Active</p>
                    <p className="text-sm text-red-600 mt-1">
                      All payments will be processed using real PayPal accounts. 
                      Make sure your subscription plans are properly configured in the PayPal Dashboard.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ============ MAIN ADMIN DASHBOARD COMPONENT ============

export default function AdminDashboard() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const resp = await axios.get(`${API}/admin/check`, { withCredentials: true });
        setIsAdmin(resp.data.is_admin);
        if (!resp.data.is_admin) {
          navigate('/dashboard');
        }
      } catch (e) {
        setIsAdmin(false);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E94560] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[var(--ink-1)] mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-4">You don't have permission to access this page.</p>
          <Link to="/dashboard" className="text-[#E94560] hover:underline">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route index element={<DashboardPage />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="promo-codes" element={<PromoCodesPage />} />
      <Route path="audits" element={<AuditsPage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route path="settings" element={<SettingsPage />} />
    </Routes>
  );
}
